import React, { useState } from "react";
import { Form, Input, Button, Typography, Row, Col, Card } from "antd";
import {
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  UserOutlined,
  MessageOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../store/context/theme-context";
import { useTenant } from "../store/context/tenant-context";
import { useNotification } from "../store/context/notification-context";
import LanguageSelector from "../shared/languages/LanguageSelector";
import TenantSwitcher from "../components/General/TenantSwitcher";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const ContactUsPage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const tenant = useTenant();
  const { theme } = useTheme();
  const { triggerNotification } = useNotification();

  const isDark = theme === "dark";
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (values) => {
    try {
      setIsLoading(true);
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      triggerNotification({
        type: "success",
        message: "Message Sent!",
        description: "Thank you for contacting us. We'll respond within 24 hours.",
      });

      setSubmitted(true);
      form.resetFields();

      // Reset submitted state after 5 seconds
      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: "Failed to send message. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <PhoneOutlined />,
      title: "Phone",
      content: tenant.contact?.phone || "+250 788 123 456",
      action: "tel:" + (tenant.contact?.phone || "+250788123456").replace(/\s/g, ""),
    },
    {
      icon: <MailOutlined />,
      title: "Email",
      content: tenant.contact?.email || "support@banklingo.com",
      action: "mailto:" + (tenant.contact?.email || "support@banklingo.com"),
    },
    {
      icon: <EnvironmentOutlined />,
      title: "Address",
      content: tenant.contact?.address || "KN 4 Ave, Kigali, Rwanda",
      action: null,
    },
    {
      icon: <ClockCircleOutlined />,
      title: "Business Hours",
      content: "Mon - Fri: 8:00 AM - 5:00 PM\nSat: 9:00 AM - 1:00 PM",
      action: null,
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: isDark
          ? `linear-gradient(135deg, #0f0c29 0%, ${tenant.theme.colorPrimary}15 50%, #24243e 100%)`
          : `linear-gradient(135deg, ${tenant.theme.colorPrimary}20 0%, ${tenant.theme.colorPrimary}40 50%, ${tenant.theme.colorPrimary}30 100%)`,
        position: "relative",
        overflow: "hidden",
        padding: "20px",
      }}
    >
      {/* Animated Background Elements */}
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          opacity: "0.1",
          background: `
            radial-gradient(circle at 20% 50%, ${tenant.theme.colorPrimary} 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, ${tenant.theme.colorPrimary} 0%, transparent 50%),
            radial-gradient(circle at 40% 20%, ${tenant.theme.colorPrimary} 0%, transparent 50%)
          `,
        }}
      />

      {/* Decorative Circles */}
      <div
        style={{
          position: "absolute",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          border: `2px solid ${tenant.theme.colorPrimary}`,
          opacity: "0.2",
          top: "-100px",
          right: "-100px",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: "200px",
          height: "200px",
          borderRadius: "50%",
          border: `2px solid ${tenant.theme.colorPrimary}`,
          opacity: "0.15",
          bottom: "-50px",
          left: "-50px",
        }}
      />

      {/* Top Bar - Language Selector and Tenant Switcher */}
      <div
        style={{
          position: "absolute",
          top: "20px",
          right: "20px",
          zIndex: 10,
          display: "flex",
          gap: "12px",
        }}
      >
        {/* Development Tenant Switcher - Only show on localhost */}
        {process.env.NODE_ENV === 'development' && 
         (window.location.hostname === 'localhost' || 
          window.location.hostname === '127.0.0.1') && (
          <TenantSwitcher />
        )}
        <LanguageSelector />
      </div>

      {/* Back Button - Top Left */}
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/landing")}
        style={{
          position: "absolute",
          top: "20px",
          left: "20px",
          color: isDark ? "#ffffff" : "#666666",
          zIndex: 10,
          fontWeight: 500,
        }}
      >
        Back to Home
      </Button>

      {/* Main Content */}
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
          paddingTop: "80px",
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <img
            src={isDark ? tenant.darkLogo : tenant.logo}
            alt={`${tenant.name} Logo`}
            style={{
              height: "100px",
              width: "auto",
              margin: "0 auto",
            }}
          />
        </div>

        {/* Title and Description */}
        <div style={{ textAlign: "center", marginBottom: "48px" }}>
          <Title
            level={1}
            style={{
              color: isDark ? "#ffffff" : "#333333",
              fontSize: "42px",
              fontWeight: "700",
              marginBottom: "16px",
            }}
          >
            Get In Touch
          </Title>
          <Paragraph
            style={{
              fontSize: "16px",
              color: isDark ? "rgba(255, 255, 255, 0.75)" : "#666666",
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            Have questions? We're here to help. Send us a message and we'll
            respond as soon as possible.
          </Paragraph>
        </div>

        {/* Two Column Layout - Form and Contact Info */}
        <Row gutter={[32, 32]}>
          {/* Contact Form */}
          <Col xs={24} lg={14}>
            <Card
              style={{
                backgroundColor: isDark
                  ? "rgba(31, 31, 31, 0.95)"
                  : "rgba(255, 255, 255, 0.98)",
                borderRadius: "20px",
                border: "none",
                boxShadow: isDark
                  ? "0 30px 80px rgba(0, 0, 0, 0.5)"
                  : "0 30px 80px rgba(0, 0, 0, 0.2)",
                backdropFilter: "blur(10px)",
              }}
            >
              {!submitted ? (
                <>
                  <Title
                    level={3}
                    style={{
                      color: isDark ? "#ffffff" : "#000000",
                      marginBottom: "8px",
                    }}
                  >
                    Send Us a Message
                  </Title>
                  <Text
                    style={{
                      color: isDark ? "#a0a0a0" : "#595959",
                      display: "block",
                      marginBottom: "32px",
                    }}
                  >
                    Fill out the form below and our team will get back to you
                    within 24 hours.
                  </Text>

                  <Form form={form} onFinish={handleSubmit} layout="vertical">
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="firstName"
                          label={
                            <span style={{ color: isDark ? "#ffffff" : "#000000" }}>
                              First Name
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Please enter your first name",
                            },
                          ]}
                        >
                          <Input
                            prefix={<UserOutlined />}
                            placeholder="John"
                            style={{
                              height: "48px",
                              borderRadius: "8px",
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="lastName"
                          label={
                            <span style={{ color: isDark ? "#ffffff" : "#000000" }}>
                              Last Name
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Please enter your last name",
                            },
                          ]}
                        >
                          <Input
                            prefix={<UserOutlined />}
                            placeholder="Doe"
                            style={{
                              height: "48px",
                              borderRadius: "8px",
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      name="email"
                      label={
                        <span style={{ color: isDark ? "#ffffff" : "#000000" }}>
                          Email Address
                        </span>
                      }
                      rules={[
                        { required: true, message: "Please enter your email" },
                        { type: "email", message: "Please enter a valid email" },
                      ]}
                    >
                      <Input
                        prefix={<MailOutlined />}
                        placeholder="john.doe@example.com"
                        style={{
                          height: "48px",
                          borderRadius: "8px",
                        }}
                      />
                    </Form.Item>

                    <Form.Item
                      name="phone"
                      label={
                        <span style={{ color: isDark ? "#ffffff" : "#000000" }}>
                          Phone Number (Optional)
                        </span>
                      }
                    >
                      <Input
                        prefix={<PhoneOutlined />}
                        placeholder="+250 788 123 456"
                        style={{
                          height: "48px",
                          borderRadius: "8px",
                        }}
                      />
                    </Form.Item>

                    <Form.Item
                      name="message"
                      label={
                        <span style={{ color: isDark ? "#ffffff" : "#000000" }}>
                          Message
                        </span>
                      }
                      rules={[
                        { required: true, message: "Please enter your message" },
                      ]}
                    >
                      <TextArea
                        prefix={<MessageOutlined />}
                        placeholder="How can we help you?"
                        rows={5}
                        style={{
                          borderRadius: "8px",
                        }}
                      />
                    </Form.Item>

                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={isLoading}
                        style={{
                          width: "100%",
                          height: "48px",
                          borderRadius: "8px",
                          fontSize: "16px",
                          fontWeight: "600",
                          backgroundColor: tenant.theme.colorPrimary,
                          borderColor: tenant.theme.colorPrimary,
                        }}
                      >
                        Send Message
                      </Button>
                    </Form.Item>
                  </Form>
                </>
              ) : (
                <div style={{ textAlign: "center", padding: "60px 20px" }}>
                  <CheckCircleOutlined
                    style={{
                      fontSize: "64px",
                      color: tenant.theme.colorPrimary,
                      marginBottom: "24px",
                    }}
                  />
                  <Title
                    level={3}
                    style={{ color: isDark ? "#ffffff" : "#000000" }}
                  >
                    Message Sent Successfully!
                  </Title>
                  <Text style={{ color: isDark ? "#a0a0a0" : "#595959" }}>
                    We've received your message and will get back to you within 24
                    hours.
                  </Text>
                </div>
              )}
            </Card>
          </Col>

          {/* Contact Information */}
          <Col xs={24} lg={10}>
            <Card
              style={{
                backgroundColor: isDark
                  ? "rgba(31, 31, 31, 0.95)"
                  : "rgba(255, 255, 255, 0.98)",
                borderRadius: "20px",
                border: "none",
                boxShadow: isDark
                  ? "0 30px 80px rgba(0, 0, 0, 0.5)"
                  : "0 30px 80px rgba(0, 0, 0, 0.2)",
                backdropFilter: "blur(10px)",
              }}
            >
              <Title
                level={3}
                style={{
                  color: isDark ? "#ffffff" : "#000000",
                  marginBottom: "8px",
                }}
              >
                Contact Information
              </Title>
              <Text
                style={{
                  color: isDark ? "#a0a0a0" : "#595959",
                  display: "block",
                  marginBottom: "32px",
                }}
              >
                Reach out to us through any of these channels.
              </Text>

              <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                {contactInfo.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => item.action && window.open(item.action, "_self")}
                    style={{
                      padding: "20px",
                      borderRadius: "12px",
                      backgroundColor: isDark
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.02)",
                      cursor: item.action ? "pointer" : "default",
                      transition: "all 0.3s ease",
                      border: `1px solid ${
                        isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.06)"
                      }`,
                    }}
                    onMouseEnter={(e) => {
                      if (item.action) {
                        e.currentTarget.style.backgroundColor = `${tenant.theme.colorPrimary}15`;
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = isDark
                          ? "0 8px 16px rgba(0, 0, 0, 0.3)"
                          : "0 8px 16px rgba(0, 0, 0, 0.1)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (item.action) {
                        e.currentTarget.style.backgroundColor = isDark
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                      <div
                        style={{
                          width: "48px",
                          height: "48px",
                          borderRadius: "12px",
                          backgroundColor: `${tenant.theme.colorPrimary}20`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {React.cloneElement(item.icon, {
                          style: { fontSize: "24px", color: tenant.theme.colorPrimary },
                        })}
                      </div>
                      <div>
                        <Text
                          strong
                          style={{
                            color: isDark ? "#ffffff" : "#000000",
                            fontSize: "16px",
                            display: "block",
                            marginBottom: "4px",
                          }}
                        >
                          {item.title}
                        </Text>
                        <Text
                          style={{
                            color: isDark ? "#a0a0a0" : "#595959",
                            fontSize: "14px",
                            whiteSpace: "pre-line",
                          }}
                        >
                          {item.content}
                        </Text>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ContactUsPage;
